/**
 * CmsContributors — expert-contributor editor.
 *
 * Lists every contributor (live + draft) with inline edit / create /
 * delete. Powers Phase 6 bylines on the cornerstone guide pages.
 *
 * Uses the same super-admin auth cookie as every other CMS surface.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Save, Plus, Trash2, Users, Loader2, X,
} from "lucide-react";

const EMPTY = {
  slug: "",
  name: "",
  role: "editor",
  credentials: "",
  bio: "",
  headshot_url: "",
  expertise_areas: [],
  reviewed_topics: [],
  corrections: [],
  status: "live",
};

const ROLES = [
  { key: "dietitian",              label: "Dietitian" },
  { key: "food-safety-specialist", label: "Food-safety specialist" },
  { key: "farmer",                 label: "Farmer" },
  { key: "editor",                 label: "Editorial team" },
];

export default function CmsContributors() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug being edited or "__new__"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/food-pulse/contributors");
      setList(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load contributors");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("__new__");
  };
  const openEdit = (c) => {
    setForm({
      slug: c.slug,
      name: c.name,
      role: c.role,
      credentials: c.credentials || "",
      bio: c.bio || "",
      headshot_url: c.headshot_url || "",
      expertise_areas: c.expertise_areas || [],
      reviewed_topics: c.reviewed_topics || [],
      corrections: c.corrections || [],
      status: c.status || "live",
    });
    setEditing(c.slug);
  };

  const save = async () => {
    if (!form.slug || !form.name) {
      toast.error("Slug and name are required");
      return;
    }
    setBusy(true);
    try {
      if (editing === "__new__") {
        await http.post("/admin/food-pulse/contributors", form);
        toast.success("Contributor created");
      } else {
        await http.put(`/admin/food-pulse/contributors/${form.slug}`, form);
        toast.success("Contributor saved");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!confirm(`Delete contributor "${slug}"?`)) return;
    try {
      await http.delete(`/admin/food-pulse/contributors/${slug}`);
      toast.success("Contributor deleted");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <ContributorEditor
        form={form}
        setForm={setForm}
        onSave={save}
        busy={busy}
        onCancel={() => setEditing(null)}
        isNew={editing === "__new__"}
      />
    );
  }

  return (
    <div className="max-w-4xl" data-testid="cms-contributors">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Expert contributors</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage the named bylines that appear on cornerstone guides —
            dietitians, food-safety specialists, farmers, and editors.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="cms-contributors-new"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="mr-1 h-4 w-4" /> New contributor
        </Button>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-6 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {list.map((c) => (
            <div key={c.slug} className="flex items-center justify-between gap-3 p-4" data-testid={`cms-contributor-row-${c.slug}`}>
              <div className="flex items-start gap-3">
                <Users className="mt-0.5 h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.role} · {c.slug} · <span className={c.status === "live" ? "text-emerald-600" : "text-amber-600"}>{c.status}</span>
                  </div>
                  {c.credentials && (
                    <div className="mt-0.5 text-[11px] text-slate-500 line-clamp-1">
                      {c.credentials}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(c)}
                  data-testid={`cms-contributor-edit-${c.slug}`}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(c.slug)}
                  data-testid={`cms-contributor-delete-${c.slug}`}
                  className="text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              No contributors yet. Add the first byline.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContributorEditor({ form, setForm, onSave, busy, onCancel, isNew }) {
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addChip = (key, val) => {
    if (!val) return;
    setList(key, [...(form[key] || []), val]);
  };
  const removeChip = (key, idx) => {
    setList(key, form[key].filter((_, i) => i !== idx));
  };

  return (
    <div className="max-w-3xl" data-testid="cms-contributor-editor">
      <button
        onClick={onCancel}
        className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to list
      </button>
      <h1 className="text-2xl font-semibold text-slate-900">
        {isNew ? "New contributor" : `Edit ${form.name || form.slug}`}
      </h1>

      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Slug (URL-safe)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, "-"))}
              disabled={!isNew}
              data-testid="cms-contributor-slug"
            />
          </div>
          <div>
            <Label className="text-xs">Full name</Label>
            <Input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              data-testid="cms-contributor-name"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <select
              value={form.role}
              onChange={(e) => setField("role", e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              data-testid="cms-contributor-role"
            >
              {ROLES.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              data-testid="cms-contributor-status"
            >
              <option value="live">Live</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Credentials (comma-separated line — shows on the card)</Label>
          <Input
            value={form.credentials}
            onChange={(e) => setField("credentials", e.target.value)}
            data-testid="cms-contributor-credentials"
          />
        </div>

        <div>
          <Label className="text-xs">Bio (plain text, 2-3 paragraphs)</Label>
          <Textarea
            rows={6}
            value={form.bio}
            onChange={(e) => setField("bio", e.target.value)}
            data-testid="cms-contributor-bio"
          />
        </div>

        <div>
          <Label className="text-xs">Headshot URL (optional)</Label>
          <Input
            value={form.headshot_url}
            onChange={(e) => setField("headshot_url", e.target.value)}
            placeholder="https://…"
            data-testid="cms-contributor-headshot"
          />
        </div>

        <ChipList
          label="Expertise areas"
          items={form.expertise_areas || []}
          onAdd={(v) => addChip("expertise_areas", v)}
          onRemove={(i) => removeChip("expertise_areas", i)}
          testid="cms-contributor-expertise"
        />
        <ChipList
          label="Reviewed guide slugs"
          placeholder="e.g. date-label-interpretation"
          items={form.reviewed_topics || []}
          onAdd={(v) => addChip("reviewed_topics", v)}
          onRemove={(i) => removeChip("reviewed_topics", i)}
          testid="cms-contributor-reviewed"
        />

        <CorrectionsEditor
          items={form.corrections || []}
          onChange={(items) => setList("corrections", items)}
        />

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={busy}
            data-testid="cms-contributor-save"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save contributor
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChipList({ label, items, onAdd, onRemove, placeholder, testid }) {
  const [val, setVal] = useState("");
  return (
    <div data-testid={testid}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200"
          >
            {it}
            <button onClick={() => onRemove(i)} className="text-emerald-700 hover:text-emerald-900">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-1">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder || "Add and press Enter"}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd(val.trim());
              setVal("");
            }
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => { onAdd(val.trim()); setVal(""); }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function CorrectionsEditor({ items, onChange }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [slug, setSlug] = useState("");

  const add = () => {
    if (!date || !note) return;
    onChange([...(items || []), { date, note, guide_slug: slug || null }]);
    setDate(""); setNote(""); setSlug("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div data-testid="cms-contributor-corrections">
      <Label className="text-xs">Corrections log</Label>
      {items?.length > 0 && (
        <ul className="mt-1 space-y-1 rounded-md bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200">
          {items.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="flex-1"><b>{c.date}</b> — {c.note}{c.guide_slug ? ` · ${c.guide_slug}` : ""}</span>
              <button onClick={() => remove(i)} className="text-amber-800 hover:text-amber-900">
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Input value={date} onChange={(e) => setDate(e.target.value)} placeholder="YYYY-MM-DD" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was corrected" />
        <div className="flex gap-1">
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="guide slug (optional)" />
          <Button variant="outline" size="sm" onClick={add}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
